import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import { Preferences } from '@capacitor/preferences';

import type { AuthStateType, LanguageType, LanguageTypeFromRacore, UserInfoType } from '@/types';
import { saveToken, removeToken, getTokens } from '@/stores/secure';
import { refreshAndStoreTokens } from '@/api/refreshToken';

const defaultUserInfo: UserInfoType = {
  id: null,
  avatar_url: null,
  username: null,
  email: null,
  firstName: null,
  lastName: null,
  language: 'fr',
  service: null,
  favorite_streaming_service: null,
  feature_flag_group: null,
  urls: {
    profile: null,
    external_urls: { spotify: null },
    app: null,
  },
};

/**
 * Async storage adapter wrapping @capacitor/preferences.
 *
 * Capacitor storage is asynchronous (unlike the legacy synchronous MMKV), so
 * the persist middleware must use an async adapter — otherwise the store can
 * hydrate with empty data on cold start. Gate first render on hydration
 * (see `useAuthHydrated`) so the app never flashes a logged-out state.
 */
const capacitorStorage: StateStorage = {
  getItem: async (name) => (await Preferences.get({ key: name })).value,
  setItem: async (name, value) => {
    await Preferences.set({ key: name, value });
  },
  removeItem: async (name) => {
    await Preferences.remove({ key: name });
  },
};

export const useAuthStore = create<AuthStateType>()(
  persist(
    (set) => ({
      userInfo: defaultUserInfo,

      login: async (bearerToken: string, refreshToken: string) => {
        await saveToken(bearerToken, refreshToken);
      },

      logout: async () => {
        try {
          await removeToken();
          set({ userInfo: defaultUserInfo });
        } catch (error) {
          // Non-critical — user can retry.
           
          console.warn('Error during logout:', error);
        }
      },

      setUserInfo: async (
        userInfo: Partial<UserInfoType> & {
          language?: LanguageType | LanguageTypeFromRacore;
        },
      ) => {
        // The Partial<UserInfoType> intersection narrows `language` to a plain
        // string, so cast back to the full union to handle the Strapi-relation
        // object form (LanguageTypeFromRacore).
        const rawLanguage = userInfo.language as LanguageType | LanguageTypeFromRacore | undefined;
        let language: LanguageType = 'fr';
        if (rawLanguage) {
          language = typeof rawLanguage === 'string' ? rawLanguage : rawLanguage.language;
        }

        set((state) => {
          // Preserve music service when the incoming patch doesn't set it.
          const preservedService: 'spotify' | null =
            'service' in userInfo ? (userInfo.service ?? null) : (state.userInfo.service ?? null);

          // avatar_url is required in DB — never let a partial update null it.
          const preservedAvatarUrl =
            'avatar_url' in userInfo
              ? userInfo.avatar_url && userInfo.avatar_url.trim() !== ''
                ? userInfo.avatar_url
                : state.userInfo.avatar_url
              : state.userInfo.avatar_url;

          // Preserve streaming preference across partial updates.
          const preservedFavoriteService =
            'favorite_streaming_service' in userInfo
              ? (userInfo.favorite_streaming_service ?? null)
              : (state.userInfo.favorite_streaming_service ?? null);

          return {
            userInfo: {
              ...state.userInfo,
              ...userInfo,
              language,
              service: preservedService,
              avatar_url: preservedAvatarUrl,
              favorite_streaming_service: preservedFavoriteService,
            },
          };
        });
      },

      refreshToken: async () => {
        const { refreshToken } = await getTokens();
        if (!refreshToken) throw new Error('No refresh token available');
        return refreshAndStoreTokens(refreshToken);
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => capacitorStorage),
      partialize: (state) => ({ userInfo: state.userInfo }),
    },
  ),
);
